import React, { useState, useRef, useEffect } from "react";
import { Check as CheckIcon, Close } from "@mui/icons-material";
import { TextField, MenuItem, Checkbox, FormControlLabel } from "@mui/material";

const EditableInput = ({
  title,
  value,
  onSave,
  disabled = false,
  inputType = "text",
  options = [],
  error = false,
  helperText = "",
  isManifest
}) => {
  const [editing, setEditing] = useState(false);
  const [currentValue, setCurrentValue] = useState(
    inputType === "multi-select" ? (Array.isArray(value) ? value : []) : value
  );
  const [showOptions, setShowOptions] = useState(false);
  const dropdownRef = useRef(null);

  // Sync value changes from parent
  useEffect(() => {
    setCurrentValue(
      inputType === "multi-select" ? (Array.isArray(value) ? value : []) : value
    );
  }, [value, inputType]);

  // Handle click outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowOptions(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [editing, currentValue]);

  const handleSave = async (e, fromOutside = false) => {
    if (!fromOutside) {
      e.preventDefault();
      e.stopPropagation();
    }

    try {
      let valueToSave = currentValue;
      if (inputType === "date" && currentValue) {
        valueToSave = new Date(currentValue).toISOString();
      }
      await onSave(valueToSave);
      setEditing(false);
      setShowOptions(false);
    } catch (error) {
      console.error("Error saving data:", error);
      setCurrentValue(value);
      setEditing(false);
      setShowOptions(false);
    }
  };

  const handleCancel = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setCurrentValue(value);
    setEditing(false);
    setShowOptions(false);
  };

  const handleSelectChange = (optionValue) => {
    if (inputType === "multi-select") {
      setCurrentValue((prev) => {
        const newValue = Array.isArray(prev) ? [...prev] : [];
        const index = newValue.indexOf(optionValue);

        if (index > -1) {
          newValue.splice(index, 1);
        } else {
          newValue.push(optionValue);
        }
        return newValue;
      });
    } else {
      setCurrentValue(optionValue);
      setShowOptions(false);
      setEditing(true);
    }
  };

  const renderInput = () => {
    let displayValue = currentValue;

    if (inputType === "date") {
      try {
        // Handle null/undefined/empty date values
        if (currentValue) {
          const date = new Date(currentValue);
          if (!isNaN(date.getTime())) {
            displayValue = date.toISOString().split("T")[0];
          } else {
            displayValue = "";
          }
        } else {
          displayValue = "";
        }
      } catch (e) {
        console.error("Error formatting date:", e);
        displayValue = "";
      }
    }

    if (inputType === "select") {
      return (
        <TextField
          select
          label={title}
          variant="outlined"
          fullWidth
          size="small"
          value={currentValue || ""}
          onChange={(e) => {
            setCurrentValue(e.target.value);
            setEditing(true);
          }}
          disabled={disabled}
          error={error}
          helperText={helperText}
          InputLabelProps={{ shrink: true }}
        >
          {options.map((option) => (
            <MenuItem key={option.value} value={option.value}>
              {option.label}
            </MenuItem>
          ))}
        </TextField>
      );
    }

    if (inputType === "multi-select") {
      const selectedLabels = options
        .filter((option) => currentValue?.includes(option.value))
        .map((option) => option.label)
        .join(", ");

      return (
        <div ref={dropdownRef} className="relative w-full">
          <TextField
            label={title}
            variant="outlined"
            fullWidth
            size="small"
            value={selectedLabels || ""}
            onClick={(e) => {
              if (disabled) return;
              e.stopPropagation();
              setShowOptions(!showOptions);
            }}
            disabled={disabled}
            error={error}
            helperText={helperText}
            InputLabelProps={{ shrink: true }}
            InputProps={{
              readOnly: true,
            }}
          />

          {showOptions && !disabled && (
            <div className="absolute top-full left-0 mt-1 w-full bg-white border border-gray-300 shadow-md rounded-md z-20 max-h-60 overflow-y-auto">
              {options.map((option) => (
                <FormControlLabel
                  key={option.value}
                  control={
                    <Checkbox
                      checked={currentValue?.includes(option.value) || false}
                      onChange={(e) => {
                        e.stopPropagation();
                        handleSelectChange(option.value);
                        setEditing(true);
                      }}
                    />
                  }
                  label={option.label}
                  className="w-full m-0 px-3 py-2 hover:bg-blue-50"
                />
              ))}
            </div>
          )}
        </div>
      );
    }

    if (inputType === "date") {
      return (
        <TextField
          type="date"
          label={title}
          variant="outlined"
          fullWidth
          size="small"
          value={displayValue}
          onChange={(e) => {
            setCurrentValue(e.target.value);
            setEditing(true);
          }}
          disabled={disabled}
          error={error}
          helperText={helperText}
          InputLabelProps={{ shrink: true }}
        />
      );
    }

    return (
      <TextField
        type={inputType}
        label={title}
        variant={isManifest ? "standard" : "outlined"}
        fullWidth
        size="small"
        value={currentValue || ""}
        onChange={(e) => {
          setCurrentValue(e.target.value);
          if (e.target.value !== value) setEditing(true);
        }}
        disabled={disabled}
        error={error}
        helperText={helperText}
        InputLabelProps={{ shrink: true }}
        sx={
          isManifest
            ? {
                "& .MuiInput-underline:before": { borderBottomColor: "transparent" },
                "& .MuiInput-underline:hover:not(.Mui-disabled):before": {
                  borderBottomColor: "rgba(0, 0, 0, 0.42)",
                },
                "& .MuiInput-underline:after": { borderBottomColor: "#1e55bf" },
                "& .MuiInputBase-input": {
                  fontSize: "1rem",
                  fontWeight: 500,
                  py: 0.5,
                },
              }
            : {}
        }
      />
    );
  };

  return (
    <div className="relative w-full">
      {renderInput()}

      {(editing || showOptions) && !disabled && (
        <div className="absolute top-full right-0 mt-1 bg-white border border-gray-300 shadow-md px-2 py-[6px] rounded flex gap-2 z-20">
          <button
            onClick={handleSave}
            className="text-green-600 cursor-pointer"
            onMouseDown={(e) => e.preventDefault()}
          >
            <CheckIcon fontSize="small" />
          </button>
          <button
            onClick={handleCancel}
            className="text-red-500 cursor-pointer text-lg leading-none"
            onMouseDown={(e) => e.preventDefault()}
          >
            <Close fontSize="small" />
          </button>
        </div>
      )}
    </div>
  );
};

export default EditableInput;
